import React, { FC, ReactNode } from "react";
import {
  Badge,
  Card,
  Col,
  Divider,
  Row,
  Space,
  Statistic,
  Tag,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { Routes } from "shared/routers";
import { overviewOverdueTagColor } from "../utils/overviewOverdueTagColor";

const { Text, Title } = Typography;

export interface AssignedItem {
  name: string;
  count: number;
}

interface BaseOverviewCardProps {
  title: string;
  coverImage?: string | null;
  coverFallbackIcon: ReactNode;
  openTasks: number;
  maxPriority?: string | number | null;
  maxDaysOverdue?: number | null;
  assignedItems: AssignedItem[];
  assignedTitle: string;
  assignedEmptyText: string;
  assignedItemIcon: ReactNode;
  filterKey: string;
  filterValue: string;
}

export const BaseOverviewCard: FC<BaseOverviewCardProps> = ({
  title,
  coverImage,
  coverFallbackIcon,
  openTasks,
  maxPriority,
  maxDaysOverdue,
  assignedItems,
  assignedTitle,
  assignedEmptyText,
  assignedItemIcon,
  filterKey,
  filterValue,
}) => {
  const { t } = useTranslation();

  const priorityStr =
    maxPriority !== null && maxPriority !== undefined
      ? String(maxPriority).trim()
      : "";
  const daysOverdueNum =
    maxDaysOverdue !== null && maxDaysOverdue !== undefined
      ? Number(maxDaysOverdue)
      : NaN;
  const hasMaxDaysOverdue = Number.isFinite(daysOverdueNum);

  const openAllTasksFiltered = () => {
    const params = new URLSearchParams({
      status: "PENDING",
      [filterKey]: filterValue,
    });
    const url = `${window.location.origin}${Routes.taskHubUserAllTasks.url}?${params}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Card
      hoverable
      className="cursor-pointer"
      onClick={openAllTasksFiltered}
      cover={
        <div className="flex h-40 w-full items-center justify-center overflow-hidden">
          {coverImage ? (
            <img
              alt={title}
              className="h-full w-full object-contain"
              src={coverImage}
            />
          ) : (
            <div className="flex justify-center items-center w-full h-full">
              {coverFallbackIcon}
            </div>
          )}
        </div>
      }
      bodyStyle={{ padding: 16 }}
    >
      <Space direction="vertical" size="middle" className="w-full">
        <Title className="!m-0 truncate" level={5} title={title}>
          {title}
        </Title>

        <Row gutter={[12, 12]} align="top">
          <Col flex="auto">
            <Statistic
              title="Open Tasks"
              value={openTasks}
              valueStyle={{ fontSize: 22, fontWeight: 600, lineHeight: 1.2 }}
            />
          </Col>
          {priorityStr || hasMaxDaysOverdue ? (
            <Col flex="none">
              <Space direction="vertical" size={8} align="end">
                {priorityStr ? (
                  <Space align="center" size={8}>
                    <Text type="secondary" className="text-xs uppercase">
                      {t("overview.maxPriority")}
                    </Text>
                    <Tag
                      className="!m-0"
                      color={
                        [
                          "#237804",
                          "#237804",
                          "#237804",
                          "#d46b08",
                          "#d46b08",
                          "#cf1322",
                        ][Number(maxPriority)] || "#cf1322"
                      }
                    >
                      {priorityStr}
                    </Tag>
                  </Space>
                ) : null}
                {hasMaxDaysOverdue ? (
                  <Space align="center" size={8}>
                    <Text type="secondary" className="text-xs uppercase">
                      {t("overview.maxDaysOverdue")}
                    </Text>
                    <Tag
                      className="!m-0"
                      color={overviewOverdueTagColor(daysOverdueNum)}
                    >
                      {daysOverdueNum}
                    </Tag>
                  </Space>
                ) : null}
              </Space>
            </Col>
          ) : null}
        </Row>

        <>
          <Divider className="!my-0" />
          <div>
            <Text
              type="secondary"
              className="mb-2 block text-xs uppercase tracking-wide"
            >
              {assignedTitle}
            </Text>
            {assignedItems.length > 0 ? (
              <ul className="m-0 flex max-h-28 list-none flex-col gap-1.5 overflow-y-auto p-0">
                {assignedItems.map(({ name, count }) => (
                  <li
                    key={name}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      {assignedItemIcon}
                      <Text className="truncate text-sm" title={name}>
                        {name}
                      </Text>
                    </span>
                    <Badge
                      className="shrink-0"
                      color="blue"
                      count={count}
                      showZero
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <Text type="secondary" className="text-sm">
                {assignedEmptyText}
              </Text>
            )}
          </div>
        </>
      </Space>
    </Card>
  );
};
